import { Check, RotateCcw, PiggyBank, Gauge, Download, Smartphone, CreditCard } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { pricingPlanMeta } from "../data/staticData";
import { useLanguage } from "../i18n/LanguageContext";

const quotaIcons = [RotateCcw, PiggyBank, Gauge];
const howToBuyIcons = [Download, Smartphone, CreditCard];

export function Pricing({ onPrimaryCta }) {
  const { t } = useLanguage();
  const p = t.pricing;

  return (
    <section id="pricing" className="relative py-24 lg:py-32 bg-[#0A0A0C]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <RevealOnScroll className="max-w-2xl mb-16">
          <span className="font-mono-data text-xs text-[#00F0FF]">/ {t.nav.pricing.toUpperCase()}</span>
          <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl text-white tracking-tighter">
            {p.title}
          </h2>
          <p className="mt-4 text-zinc-400 text-base sm:text-lg">{p.subtitle}</p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.05} className="mb-16" data-testid="pricing-quota-explainer">
          <h3 className="font-display font-semibold text-lg text-white mb-6">{p.quotaTitle}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {p.quotaSteps.map((step, i) => {
              const Icon = quotaIcons[i];
              return (
                <div
                  key={step.title}
                  className="rounded-xl border border-white/10 bg-[#050505] p-6"
                  data-testid={`pricing-quota-step-${i}`}
                >
                  <Icon className="w-6 h-6 text-[#00F0FF] mb-4" strokeWidth={1.5} />
                  <h4 className="font-display font-semibold text-white">{step.title}</h4>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </RevealOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {p.plans.map((plan, i) => {
            const meta = pricingPlanMeta[i];
            return (
              <RevealOnScroll key={meta.id} delay={i * 0.1}>
                <div
                  className={`relative rounded-2xl p-8 h-full flex flex-col ${
                    meta.highlighted
                      ? "border-2 border-[#00F0FF] bg-[#00F0FF]/[0.04]"
                      : "border border-white/10 bg-[#050505]"
                  }`}
                  data-testid={`pricing-card-${meta.id}`}
                >
                  {meta.highlighted && (
                    <span className="absolute -top-3 left-8 font-mono-data text-[10px] px-2.5 py-1 rounded bg-[#00F0FF] text-black font-bold">
                      {p.popularBadge}
                    </span>
                  )}
                  <h3 className="font-display font-bold text-xl text-white">{plan.name}</h3>
                  <div className="mt-5 font-mono-data text-3xl sm:text-4xl font-bold text-white">
                    ${meta.monthlyPrice}
                    <span className="text-sm text-zinc-500"> {p.perMonth}</span>
                  </div>
                  <div className="mt-2 font-mono-data text-sm text-[#00F0FF]" data-testid={`pricing-quota-${meta.id}`}>
                    {meta.quotaMinutes} {p.minutesLabel}
                  </div>

                  <ul className="mt-6 space-y-3 flex-1">
                    {p.includedFeatures.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-[#00FF66] mt-0.5 shrink-0" strokeWidth={2} />
                        {f}
                      </li>
                    ))}
                    {i > 0 && (
                      <li className="flex items-start gap-2.5 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-[#00FF66] mt-0.5 shrink-0" strokeWidth={2} />
                        {p.prioritySupportBadge}
                      </li>
                    )}
                  </ul>

                  <a
                    href="#how-to-buy"
                    data-testid={`pricing-cta-${meta.id}`}
                    className={`mt-8 w-full text-center py-3 rounded-md font-semibold text-sm transition-all duration-200 ${
                      meta.highlighted
                        ? "bg-[#00F0FF] text-black hover:brightness-110"
                        : "border border-white/15 text-white hover:border-[#00F0FF]/50 hover:text-[#00F0FF]"
                    }`}
                  >
                    {plan.cta}
                  </a>
                </div>
              </RevealOnScroll>
            );
          })}
        </div>

        <RevealOnScroll delay={0.15} className="mt-20" data-testid="pricing-topup-section">
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">{p.topUpTitle}</h3>
          <p className="mt-3 text-zinc-400 max-w-2xl">{p.topUpSubtitle}</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {p.packs.map((pack, i) => {
              const meta = pricingPlanMeta[i];
              return (
                <div
                  key={meta.id}
                  className="rounded-xl border border-white/10 bg-[#050505] p-6 flex items-center justify-between gap-4"
                  data-testid={`pricing-topup-pack-${meta.id}`}
                >
                  <div>
                    <div className="font-display font-semibold text-white">{pack.name}</div>
                    <div className="mt-1 font-mono-data text-sm text-[#00F0FF]">+{meta.quotaMinutes} {p.minutesLabel}</div>
                  </div>
                  <div className="font-mono-data text-xl font-bold text-white">${meta.monthlyPrice}</div>
                </div>
              );
            })}
          </div>

          <p className="mt-5 text-sm text-zinc-500 max-w-2xl" data-testid="pricing-topup-note">{p.topUpNote}</p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.2} className="mt-16 rounded-xl border border-white/10 bg-[#050505] p-6 max-w-2xl" data-testid="pricing-free-demo">
          <h4 className="font-display font-semibold text-white">{p.freeDemoTitle}</h4>
          <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.freeDemoText}</p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.25} id="how-to-buy" className="mt-20" data-testid="pricing-how-to-buy">
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">{p.howToBuyTitle}</h3>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {p.howToBuySteps.map((step, i) => {
              const Icon = howToBuyIcons[i];
              return (
                <div key={step.title} className="flex flex-col items-start" data-testid={`pricing-how-to-buy-step-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-data text-xs px-2 py-1 rounded bg-white/10 text-zinc-400">{i + 1}</span>
                    <Icon className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.5} />
                  </div>
                  <h4 className="mt-3 font-display font-semibold text-white">{step.title}</h4>
                  <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>

          <button
            onClick={onPrimaryCta}
            data-testid="pricing-google-play-button"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#00F0FF] text-black font-semibold hover:brightness-110 hover:scale-[1.03] transition-all duration-200"
          >
            {t.site.ctaPrimary}
          </button>

          <p className="mt-4 text-xs text-zinc-500 max-w-2xl">{p.manageNote}</p>
        </RevealOnScroll>

        <p className="mt-10 text-xs text-zinc-500 max-w-2xl">{p.footnote}</p>
      </div>
    </section>
  );
}
